import React, { useState } from "react";

import {
  Box,
  Button,
  FormControlLabel,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  useMediaQuery,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
} from "@material-ui/core";

import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { useNotify } from "react-admin";

import { useWorkOrderContext } from "./context";
import { performUpdateInboundMini } from "../../../graphql/inbound/inboundCRUD";
import { logError } from "../../../utils/appUtils";

export const ExceptionDialog = ({
  saveWorkOrder,
  isExceptionDialogOpen,
  setIsExceptionDialogOpen,
  classes,
}) => {
  const [otherExceptionNoteError, setOtherExceptionError] = useState();
  const { state, dispatch } = useWorkOrderContext();
  const { currentShipment } = state;
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const notify = useNotify();

  const handleSave = async () => {
    if (!validate()) return;
    const hasAnyException =
      currentShipment.unsafeShipment ||
      currentShipment.brokenSeal ||
      currentShipment.otherException;
    currentShipment.hasException = Boolean(hasAnyException);
    if (hasAnyException) {
      currentShipment.verificationRequired = "true";
    }

    try {
      // unsafeShipment* live on InboundShipment, not WorkOrder. Persist them
      // to the linked inbound and keep work-order-only exception fields on
      // the work order. Both writes must succeed before closing the dialog.
      const inboundId =
        currentShipment.inboundShipmentId ||
        currentShipment.inputInventory?.inboundShipmentId;
      if (!inboundId) {
        throw new Error(
          "ExceptionDialog: current work order has no linked inboundShipmentId",
        );
      }
      await performUpdateInboundMini({
        id: inboundId,
        unsafeShipment: Boolean(currentShipment.unsafeShipment),
        unsafeShipmentNote: currentShipment.unsafeShipmentNote || "",
        hasException: currentShipment.hasException,
      });

      await saveWorkOrder({
        id: currentShipment.id,
        hasException: currentShipment.hasException,
        verificationRequired: currentShipment.verificationRequired,
        brokenSeal: Boolean(currentShipment.brokenSeal),
        brokenSealNote: currentShipment.brokenSealNote || "",
        otherException: Boolean(currentShipment.otherException),
        otherExceptionNote: currentShipment.otherExceptionNote || "",
      });
      setIsExceptionDialogOpen(false);
    } catch (err) {
      logError("workOrder:ExceptionDialog:handleSave", err, notify);
    }
  };

  const validate = () => {
    let isValid = true;
    setOtherExceptionError("");
    if (currentShipment.otherException) {
      isValid = isValid && currentShipment.otherExceptionNote;
      !currentShipment.otherExceptionNote &&
        setOtherExceptionError("A note is required for other exceptions");
    }
    return isValid;
  };

  const handleChange = (evt) => {
    const type = evt.target.name;
    const value = evt.target.checked;
    if (type === "unsafeShipment") {
      currentShipment.unsafeShipment = value;
      if (!value) {
        currentShipment.unsafeShipmentNote = "";
      }
    } else if (type === "brokenSeal") {
      currentShipment.brokenSeal = value;
      if (!value) {
        currentShipment.brokenSealNote = "";
      }
    } else {
      currentShipment.otherException = value;
      if (!value) {
        currentShipment.otherExceptionNote = "";
        setOtherExceptionError("");
      }
    }
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: currentShipment,
    });
  };

  const handleCancel = () => {
    setIsExceptionDialogOpen(false);
  };

  const setNote = (prop, value) => {
    currentShipment[prop] = value;
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: currentShipment,
    });
  };

  return (
    <Dialog
      open={isExceptionDialogOpen}
      maxWidth={isSmall ? "xl" : "md"}
      fullWidth={true}
    >
      <Box
        component="span"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        m={2}
      >
        <Typography variant="h6">
          {currentShipment.accountName}
        </Typography>
        <CancelOutlinedIcon fontSize="large" onClick={handleCancel} />
      </Box>
      <DialogContent>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel>Exception Type</FormLabel>
          <FormGroup row={!isSmall}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentShipment.unsafeShipment}
                  onChange={handleChange}
                  name="unsafeShipment"
                />
              }
              label="Unsafe Work Order"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentShipment.brokenSeal}
                  onChange={handleChange}
                  name="brokenSeal"
                />
              }
              label="Broken seal"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentShipment.otherException}
                  onChange={handleChange}
                  name="other"
                />
              }
              label="Other"
            />
          </FormGroup>
        </FormControl>
        {currentShipment.unsafeShipment && (
          <div className={classes.exceptionContainer}>
            <Typography>Unsafe Work Order</Typography>
            <TextField
              name="unSafeNotes"
              label="Notes"
              multiline
              autoFocus
              minRows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={
                currentShipment.unsafeShipmentNote
                  ? currentShipment.unsafeShipmentNote
                  : ""
              }
              onChange={(evt) =>
                setNote("unsafeShipmentNote", evt.target.value)
              }
            />
          </div>
        )}
        {currentShipment.brokenSeal && (
          <div className={classes.exceptionContainer}>
            <Typography>Broken Seal</Typography>
            <TextField
              name="brokenNotes"
              label="Notes"
              multiline
              autoFocus
              minRows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={
                currentShipment.brokenSealNote
                  ? currentShipment.brokenSealNote
                  : ""
              }
              onChange={(evt) => setNote("brokenSealNote", evt.target.value)}
            />
          </div>
        )}
        {currentShipment.otherException && (
          <div className={classes.exceptionContainer}>
            <Typography>Other</Typography>
            <TextField
              name="otherNotes"
              label="Notes"
              multiline
              autoFocus
              minRows={3}
              error={!!otherExceptionNoteError}
              helperText={otherExceptionNoteError}
              margin="dense"
              variant="outlined"
              required
              fullWidth
              value={
                currentShipment.otherExceptionNote
                  ? currentShipment.otherExceptionNote
                  : ""
              }
              onChange={(evt) =>
                setNote("otherExceptionNote", evt.target.value)
              }
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Box>
          <Box>
            <Button onClick={() => handleCancel()}>Cancel</Button>
            <Button type="submit" onClick={() => handleSave()} color="primary">
              Submit
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
